import React from 'react';

import * as styles from './styles';

import type { UUID, Vehicle, VehicleGroup } from '@cobliteam/common-types';

import { SettingsType } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import { CaptionBold, CaptionDefault, type CheckboxOption, Chip, FilterMultipleDropdown, InputLabel, type OptionKey, VehicleGroupFilter } from '@cobliteam/base-components';

import { MultiSelectVehicles } from '@cobliteam/ui/business';

export interface FiltersSectionProps {
  vehicleGroups: VehicleGroup[];
  vehicles: Vehicle[];
  wantedAlertTypes: SettingsType[];
  wantedGroups: VehicleGroup[];
  wantedVehicleIds: UUID[];
  onAlertFilterChanged: (alertTypes: SettingsType[]) => void;
  onVehicleGroupFilterChanged: (vehicleGroups: VehicleGroup[]) => void;
  onVehicleFilterChanged: (vehicleIds: UUID[]) => void;
}

enum FilterOption {
  ALERT = 'ALERT',
  GROUP = 'GROUP',
  VEHICLE = 'VEHICLE',
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({ vehicleGroups, vehicles, wantedAlertTypes, wantedGroups, wantedVehicleIds, ...props }) => {
  const [filterOpened, setFilterOpened] = React.useState<FilterOption | undefined>(undefined);

  const alertTypes = new Set(Object.values(SettingsType));

  const getAlertTypeLabel = (type: SettingsType) => {
    switch (type) {
      case SettingsType.ROAD_SPEED_LIMIT:
        return 'Excesso de velocidade da via';
      case SettingsType.VEHICLE_SPEED_LIMIT:
        return 'Excesso de velocidade do veículo';
      case SettingsType.STOP_IDLE_TIME:
        return 'Parada com motor ocioso';
      default:
        return '';
    }
  };

  const getAlertCheckboxOptions = () =>
    Array.from(alertTypes).map((type) => ({
      key: type,
      label: getAlertTypeLabel(type),
    }));

  const getVehiclesCheckboxOptions = () => {
    const allVehicles = new Map<UUID, string>();

    vehicles.forEach((vehicle) => allVehicles.set(vehicle.id, vehicle.label));
    return Array.from(allVehicles.entries()).map(([vehicleId, label]) => ({
      key: vehicleId,
      label,
    }));
  };

  const handleAlertTypeFilterApply = (selectedAlertTypes: CheckboxOption[]) => {
    const newAlertTypes = selectedAlertTypes.map((type) => type.key as SettingsType);
    setFilterOpened(undefined);
    props.onAlertFilterChanged(newAlertTypes);
  };

  const handleGroupFilterApply = (selectedVehicleGroups: VehicleGroup[]) => {
    setFilterOpened(undefined);

    Analytics.track('Vehicle Group Filter Changed In Alerts', {
      amount: selectedVehicleGroups.length,
    });
    props.onVehicleGroupFilterChanged(selectedVehicleGroups);
  };

  const handleVehicleFilterApply = (selectedVehicles: Vehicle[]) => {
    const vehicleIds = selectedVehicles.map((vehicle) => `${vehicle.id}`);
    setFilterOpened(undefined);
    props.onVehicleFilterChanged(vehicleIds);
  };

  const handleClearFilter = (filterType: string) => {
    Analytics.track(`Cleared ${filterType} Filter On User Alerts History`);
  };

  const handleAlertTypeChipDelete = (deletedAlertType: SettingsType) => {
    const newWantedAlertTypes = wantedAlertTypes.filter((type) => type !== deletedAlertType);
    props.onAlertFilterChanged(newWantedAlertTypes);
  };

  const handleGroupChipDelete = (deletedVehicleGroupId: UUID) => {
    const newWantedVehicleGroups = wantedGroups.filter((group) => group.id !== deletedVehicleGroupId);
    props.onVehicleGroupFilterChanged(newWantedVehicleGroups);
  };

  const handleVehicleChipDelete = (deletedVehicleId: OptionKey) => {
    const newWantedVehicleIds = wantedVehicleIds.filter((vehicleId) => vehicleId !== deletedVehicleId);
    props.onVehicleFilterChanged(newWantedVehicleIds);
  };

  const vehiclesOptions = getVehiclesCheckboxOptions();
  const wantedGroupIds = wantedGroups.map((group) => group.id);

  return (
    <>
      <div className={styles.filtersSection}>
        <FilterMultipleDropdown
          overlayClassName={styles.filterOverlay}
          options={getAlertCheckboxOptions()}
          selectedOptionKeys={new Set(wantedAlertTypes)}
          isOpen={filterOpened === FilterOption.ALERT}
          buttonTitle='Filtrar alertas'
          overlayTitle='Tipos de evento'
          onApply={handleAlertTypeFilterApply}
          onDropdownClick={() => setFilterOpened(FilterOption.ALERT)}
          onClear={() => handleClearFilter('Alerts')}
          onCancel={() => setFilterOpened(undefined)}
          withActionButtons
        />
        <VehicleGroupFilter
          vehicleGroups={vehicleGroups}
          appliedVehicleGroups={new Set(wantedGroupIds)}
          isOpen={filterOpened === FilterOption.GROUP}
          applyVehicleGroupsFilter={handleGroupFilterApply}
          onDropdownClick={() => setFilterOpened(FilterOption.GROUP)}
          onCancel={() => setFilterOpened(undefined)}
          onClear={() => handleClearFilter('Groups')}
        />
        <MultiSelectVehicles
          selectedVehicles={vehicles.filter((v) => wantedVehicleIds?.includes(v.id))}
          onChange={handleVehicleFilterApply}
          onClear={() => handleClearFilter('Vehicles')}
        />
        <div className={styles.severityLegend}>
          <CaptionBold>Gravidade</CaptionBold>
          <CaptionDefault>Médio</CaptionDefault>
          <CaptionDefault>Grave</CaptionDefault>
          <CaptionDefault>Gravíssimo</CaptionDefault>
        </div>
      </div>
      {Boolean(wantedGroups.length || wantedVehicleIds.length || wantedAlertTypes.length) && (
        <div className={styles.chipsWrapper}>
          <InputLabel>Visualizando:</InputLabel>
          {wantedAlertTypes.map((type, index) => (
            <Chip key={index} label={getAlertTypeLabel(type)} onDelete={() => handleAlertTypeChipDelete(type)} />
          ))}
          {wantedGroups.map((group, index) => (
            <Chip key={index} label={group.name} onDelete={() => handleGroupChipDelete(group.id)} disabled={!!wantedVehicleIds.length} />
          ))}
          {wantedVehicleIds.map((vehicleId, index) => {
            const chipOption = vehiclesOptions.find(({ key }) => key === vehicleId);
            const chipLabel = chipOption?.label;
            return <Chip key={index} label={chipLabel} onDelete={() => handleVehicleChipDelete(vehicleId)} />;
          })}
        </div>
      )}
    </>
  );
};
